import { formatDistanceToNow } from 'date-fns'
import { Key } from 'lucide-react'
import { useState } from 'react'
import { useApiKeys, useRevokeApiKey } from '@/api/hooks/use-api-keys'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import { ApiKeyUsageSection } from '@/pages/api-key-usage'
import { CreateKeyModal } from '@/pages/api-keys-create-modal'

export function ApiKeysPage(): React.JSX.Element {
  const { data, isLoading } = useApiKeys()
  const revokeKey = useRevokeApiKey()
  const [showCreate, setShowCreate] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; name: string } | null>(null)

  const keys = data?.data ?? []

  function handleRevoke(): void {
    if (!revokeTarget) return
    revokeKey.mutate(revokeTarget.id, { onSuccess: () => setRevokeTarget(null) })
  }

  if (isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading API keys...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">API Keys</h1>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          + Create Key
        </button>
      </div>

      {keys.length === 0 ? (
        <EmptyKeys onCreateClick={() => setShowCreate(true)} />
      ) : (
        <DataTable
          columns={[
            {
              key: 'name',
              label: 'Name',
              render: (row) => <span className="font-medium">{row.name}</span>,
            },
            {
              key: 'key',
              label: 'Key',
              render: (row) => <code className="font-mono text-xs">{row.key}</code>,
            },
            {
              key: 'scope',
              label: 'Scope',
              render: (row) => <StatusBadge status={row.scope}>{row.scope}</StatusBadge>,
            },
            {
              key: 'type',
              label: 'Type',
              render: (row) => (
                <StatusBadge status={row.type === 'live' ? 'active' : 'pending'}>
                  {row.type}
                </StatusBadge>
              ),
            },
            {
              key: 'last_used_at',
              label: 'Last Used',
              render: (row) => (
                <span className="text-text-secondary">
                  {row.last_used_at
                    ? formatDistanceToNow(new Date(row.last_used_at), { addSuffix: true })
                    : 'Never'}
                </span>
              ),
            },
            {
              key: 'actions',
              label: '',
              render: (row) => (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setRevokeTarget({ id: row.id, name: row.name })
                  }}
                  className="rounded-md px-3 py-1 text-sm text-danger hover:bg-red-50"
                >
                  Revoke
                </button>
              ),
            },
          ]}
          data={keys}
        />
      )}

      <ApiKeyUsageSection />

      {showCreate && <CreateKeyModal onClose={() => setShowCreate(false)} />}

      {revokeTarget && (
        <RevokeConfirmation
          name={revokeTarget.name}
          isPending={revokeKey.isPending}
          onConfirm={handleRevoke}
          onCancel={() => setRevokeTarget(null)}
        />
      )}
    </div>
  )
}

function EmptyKeys({ onCreateClick }: { onCreateClick: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Key className="mb-4 h-12 w-12 text-text-tertiary" />
      <h3 className="text-lg font-medium text-text-primary">No API keys yet</h3>
      <p className="mt-1 max-w-sm text-sm text-text-secondary">
        Create an API key to start sending emails programmatically.
      </p>
      <button
        type="button"
        onClick={onCreateClick}
        className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        Create your first key
      </button>
    </div>
  )
}

function RevokeConfirmation({
  name,
  isPending,
  onConfirm,
  onCancel,
}: {
  name: string
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-surface-0 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-text-primary">Revoke API Key</h3>
        <p className="mt-2 text-sm text-text-secondary">
          Revoking &ldquo;{name}&rdquo; will immediately invalidate it. Any application using this
          key will stop working.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
          >
            {isPending ? 'Revoking...' : 'Revoke Key'}
          </button>
        </div>
      </div>
    </div>
  )
}
